import React, { useState } from 'react';
import { X, Sparkles, Clock, Target, Check, Dumbbell } from 'lucide-react';
import { generateSmartWorkout } from '../services/aiService';

const MUSCLE_CATEGORIES = [
  'Épaules', 'Biceps', 'Triceps', 'Dos', 'Pectoraux', 
  'Abdos', 'Quadriceps', 'Ischio-jambiers', 'Mollets', 'Fessier'
];

const SmartBuilderModal = ({ onClose, onSave, onDirectSave, allExercises }) => {
  const [time, setTime] = useState(45);
  const [selectedMuscles, setSelectedMuscles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatedRoutine, setGeneratedRoutine] = useState(null);
  const [equipmentFilter, setEquipmentFilter] = useState('all');

  const toggleMuscle = (muscle) => {
    if (selectedMuscles.includes(muscle)) {
      setSelectedMuscles(selectedMuscles.filter(m => m !== muscle));
    } else {
      setSelectedMuscles([...selectedMuscles, muscle]);
    }
  };

  const getFilteredExercisesForAI = () => {
    if (equipmentFilter === 'all') return allExercises;
    if (equipmentFilter === 'home') {
      const allowed = ['Haltères', 'Poids du corps'];
      return allExercises.filter(ex => !ex.equipment || allowed.includes(ex.equipment));
    }
    if (equipmentFilter === 'bodyweight') {
      const allowed = ['Poids du corps'];
      return allExercises.filter(ex => !ex.equipment || allowed.includes(ex.equipment));
    }
    return allExercises;
  };

  const handleGenerate = async () => {
    if (selectedMuscles.length === 0) return;
    setLoading(true);
    setGeneratedRoutine(null); // Clear previous errors or routines
    
    try {
      const filteredExercises = getFilteredExercisesForAI();
      const result = await generateSmartWorkout(time, selectedMuscles, filteredExercises, equipmentFilter);
      if (result && result.exercises && result.exercises.length > 0) {
        setGeneratedRoutine(result);
      } else {
        throw new Error("Génération vide ou échouée.");
      }
    } catch (err) {
      console.error(err);
      alert("L'IA a eu un petit hoquet. Réessayez de générer !");
    }
    
    setLoading(false);
  };

  const getFormattedRoutine = () => {
    if (!generatedRoutine) return null;
    
    // Convert AI response to proper routine format
    const formattedExercises = generatedRoutine.exercises.map(ex => {
      const dbExercise = allExercises.find(e => e.id === ex.exerciseId);
      if (!dbExercise) return null;
      
      return {
        id: crypto.randomUUID(),
        exerciseId: dbExercise.id,
        name: dbExercise.name,
        equipment: dbExercise.equipment || 'Autre',
        muscles: dbExercise.muscles || (dbExercise.muscle ? [dbExercise.muscle] : ['Autre']),
        sets: Array.from({ length: parseInt(ex.sets) || 1 }, () => ({
          reps: '',
          weight: '',
          isDropSet: false
        }))
      };
    }).filter(Boolean);

    return {
      name: generatedRoutine.routineName || "Séance IA",
      exercises: formattedExercises.map(ex => ({ type: 'single', ...ex }))
    };
  };

  const handleSave = () => {
    const routine = getFormattedRoutine();
    if (routine) onSave(routine);
  };

  const handleDirectSave = () => {
    const routine = getFormattedRoutine();
    if (routine && onDirectSave) onDirectSave(routine);
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem' }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, color: 'var(--primary-color)' }}>
            <Sparkles size={24} /> Smart Builder IA
          </h2>
          <button className="btn-icon" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {!generatedRoutine ? (
          <div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
              Dis à l'IA combien de temps tu as et ce que tu veux travailler, elle construira la séance parfaite pour toi.
            </p>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                <Clock size={18} /> Temps disponible ({time} min)
              </label>
              <input 
                type="range" 
                min="15" 
                max="150" 
                step="5"
                value={time}
                onChange={(e) => setTime(Number(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--primary-color)' }}
              />
              <div style={{ position: 'relative', height: '20px', fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                <span style={{ position: 'absolute', left: 0 }}>15 min</span>
                <span style={{ position: 'absolute', left: '44.4%', transform: 'translateX(-50%)' }}>1h15</span>
                <span style={{ position: 'absolute', right: 0 }}>2h30</span>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'bold', marginBottom: '0.8rem' }}>
                <Dumbbell size={18} /> Matériel à disposition
              </label>
              <select 
                className="form-control" 
                value={equipmentFilter} 
                onChange={(e) => setEquipmentFilter(e.target.value)}
                style={{ width: '100%', padding: '0.8rem', backgroundColor: 'var(--bg-color)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: 'var(--border-radius-sm)' }}
              >
                <option value="all">🏢 Salle de sport complète</option>
                <option value="home">🏠 À la maison (Haltères, Poids du corps)</option>
                <option value="bodyweight">🤸‍♂️ Poids du corps uniquement</option>
              </select>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                <Target size={18} /> Muscles ciblés
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {MUSCLE_CATEGORIES.map(muscle => (
                  <button
                    key={muscle}
                    onClick={() => toggleMuscle(muscle)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: 'var(--border-radius-sm)',
                      border: `1px solid ${selectedMuscles.includes(muscle) ? 'var(--primary-color)' : 'var(--border-color)'}`,
                      backgroundColor: selectedMuscles.includes(muscle) ? 'rgba(162, 155, 254, 0.1)' : 'transparent',
                      color: selectedMuscles.includes(muscle) ? 'var(--primary-color)' : 'var(--text-primary)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontWeight: selectedMuscles.includes(muscle) ? 'bold' : 'normal'
                    }}
                  >
                    {muscle}
                  </button>
                ))}
              </div>
            </div>

            <button 
              className="btn btn-primary" 
              style={{ width: '100%', padding: '1rem', fontSize: '1.1rem', opacity: selectedMuscles.length === 0 ? 0.5 : 1 }}
              onClick={handleGenerate}
              disabled={selectedMuscles.length === 0 || loading}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <div className="spinner" style={{ width: '20px', height: '20px', borderTopColor: '#fff' }}></div>
                  Génération en cours...
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <Sparkles size={20} /> Générer ma séance
                </span>
              )}
            </button>
          </div>
        ) : (
          <div>
            <div style={{ backgroundColor: 'rgba(162, 155, 254, 0.1)', padding: '1.5rem', borderRadius: 'var(--border-radius)', marginBottom: '1.5rem', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--primary-color)' }}>{generatedRoutine.routineName}</h3>
              <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                {generatedRoutine.exercises.length} exercices • ~{time} minutes
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
              {generatedRoutine.exercises.map((ex, idx) => {
                const dbEx = allExercises.find(e => e.id === ex.exerciseId);
                if (!dbEx) return null;
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', backgroundColor: 'var(--bg-color)', borderRadius: 'var(--border-radius-sm)' }}>
                    <div>
                      <h4 style={{ margin: '0 0 0.3rem 0' }}>{dbEx.name}</h4>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{ex.sets} séries</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <button 
                className="btn btn-primary" 
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                onClick={handleDirectSave}
              >
                <Check size={20} /> Enregistrer
              </button>
              <button 
                className="btn" 
                style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                onClick={handleSave}
              >
                Retoucher
              </button>
            </div>
            
            <button 
              className="btn" 
              style={{ width: '100%', backgroundColor: 'transparent', border: '1px dashed var(--text-secondary)', color: 'var(--text-secondary)', marginTop: '0.5rem' }}
              onClick={() => setGeneratedRoutine(null)}
            >
              Refaire (Autre tirage)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartBuilderModal;
